<template>
	<div
		class="editable-field"
		v-auto-stack-long-value
		:class="{ 'is-missing': isMissing(value), 'is-readonly': readonly }"
	>
		<ArkHoverCard
			v-if="tooltip && !hideTitle"
			:positioning="tooltipPositioning"
			:open-delay="200"
			:close-delay="100"
			:show-arrow="true"
		>
			<template #trigger>
				<span class="title has-tooltip" :class="{ info }">{{ title ?? name }}</span>
			</template>
			<div class="tooltip-content">{{ tooltip }}</div>
		</ArkHoverCard>
		<span v-else-if="!hideTitle" class="title" :class="{ info }">{{ title ?? name }}</span>

		<span class="value" :class="{ 'has-symbol': !!symbol, readonly }">
			<span
				v-if="readonly && (isMissing(value) || (type !== 'file' && type !== 'url'))"
				class="value-text"
				:class="{ empty: isMissing(value) }"
			>
				{{ displayValue }}
			</span>

			<input
				v-else-if="type === 'text'"
				type="text"
				:name="name"
				:value="value"
				@input="onTextInput"
				:disabled="disabled"
			/>

			<span v-else-if="type === 'url' && !readonly && !Array.isArray(value)" class="url-field">
				<input type="url" :name="name" :value="value" @input="onTextInput" :disabled="disabled" />
				<a
					v-if="urlHref"
					class="open-link"
					:href="urlHref"
					target="_blank"
					rel="noopener noreferrer"
					:title="t('transaction.card.openDocument')"
				>
					<AppIcon :name="IconName.ArrowUpRight" :size="14" :stroke-width="2" />
				</a>
			</span>

			<input
				v-else-if="type === 'number'"
				type="text"
				inputmode="decimal"
				:name="name"
				:value="numberInputValue"
				placeholder="N/A"
				@focus="onNumberFocus"
				@blur="onNumberBlur"
				@input="onNumberInput"
				:disabled="disabled"
				:step="rounding"
			/>

			<SingleDatePicker
				v-else-if="type === 'date'"
				v-model="dateModel"
				:locale="datePickerLocale"
				:startOfWeek="1"
				:disabled="disabled"
				:class="{ disabled }"
			/>

			<a
				v-if="type === 'file' && !Array.isArray(value) && value"
				:href="String(value)"
				target="_blank"
				rel="noopener noreferrer"
			>
				{{ name }}
			</a>

			<div v-else-if="type === 'file' && Array.isArray(value)" class="link-list">
				<a
					v-for="(url, index) in value"
					:key="index"
					:href="url"
					target="_blank"
					rel="noopener noreferrer"
					class="link-item"
				>
					{{ value.length > 1 ? `${index + 1}` : "" }}
				</a>
			</div>

			<a
				v-else-if="readonly && type === 'url' && !Array.isArray(value) && value"
				class="link-pill"
				:href="urlHref"
				:download="download ? '' : undefined"
				:target="download ? undefined : '_blank'"
				rel="noopener noreferrer"
			>
				{{ download ? t("transaction.card.downloadDocument") : t("transaction.card.openDocument") }}
				<AppIcon v-if="download" :name="IconName.Download" :size="12" :stroke-width="2" />
				<AppIcon v-else :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
			</a>

			<div v-else-if="type === 'url' && Array.isArray(value)" class="link-list">
				<a
					v-for="(url, index) in value"
					:key="index"
					:href="String(url)"
					target="_blank"
					rel="noopener noreferrer"
					class="link-item"
				>
					{{ url }}
				</a>
			</div>

			<ArkListboxDropdown
				v-else-if="type === 'select' && !readonly"
				v-model="selectValue"
				:options="listOptions"
				:multiple="multiple"
				:disabled="disabled"
				:clearable="true"
				:searchable="false"
				:placeholder="placeholder ?? t('transaction.card.noSelection')"
			/>

			<ArkListboxDropdown
				v-else-if="type === 'checkbox' && !readonly"
				v-model="checkboxValue"
				:options="checkboxOptions"
				:multiple="false"
				:disabled="disabled"
				:clearable="true"
				:placeholder="t('transaction.card.noSelection')"
			/>

			<textarea v-else-if="type === 'textarea' && !readonly" :name="name" @input="onTextInput" :disabled="disabled">{{
				value
			}}</textarea>

			<span v-if="symbol" class="symbol">{{ symbol }}</span>
		</span>
		<CopyButton v-if="copyTitle && !isMissing(value)" class="copy" :text="displayValue" :title="copyTitle" size="md" />
	</div>

	<div v-if="isMultiMode" class="multi-values-section">
		<button
			v-if="aggregationType === 'choice' && !isBreakdownAutoExpanded"
			type="button"
			class="values-breakdown-toggle"
			@click="manualBreakdownExpanded = !isBreakdownExpanded"
		>
			{{
				isBreakdownExpanded
					? t("transaction.card.collapseValues")
					: t("transaction.card.valuesBreakdownToggle", { count: mergedMultiValues?.length })
			}}
		</button>
		<dl
			v-if="aggregationType === 'choice' && isBreakdownExpanded"
			class="values-breakdown"
			:class="{ 'simple-list': isSimpleValuesList }"
		>
			<div
				v-for="item in mergedMultiValues"
				:key="item.ids.join(',')"
				class="choice-option"
				:class="{ disabled: disabled, 'full-width': !isSimpleValuesList, linked: !!item.href }"
				@click="disabled ? undefined : onChoiceSelect(item.value)"
			>
				<dt v-if="!isSimpleValuesList" class="transaction-label">
					<a v-if="item.href" :href="item.href" target="_blank" rel="noopener noreferrer" @click.stop>
						{{ formatIdsList(item.ids) }}
					</a>
					<template v-else>{{ formatIdsList(item.ids) }}</template>
				</dt>
				<dd v-if="!item.href" class="choice-value">{{ item.formattedValue }}</dd>
			</div>
		</dl>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, getCurrentInstance } from "vue"
import debounce from "lodash.debounce"
import { useI18n } from "vue-i18n"

import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import SingleDatePicker from "../ui/SingleDatePicker.vue"
import ArkListboxDropdown from "../ui/ArkListboxDropdown.vue"
import ArkHoverCard from "../ui/ArkHoverCard.vue"
import CopyButton from "../ui/CopyButton.vue"
import { parseISODate, formatDateToISO } from "@/utils/date"
import { formatQuantity, parseQuantityInput, roundTo, toExternalHref } from "@/utils/format"
import { UseRootStore } from "@/model/RootStore"

const store = UseRootStore()
const datePickerLocale = computed(() => (store.currentLocale === "fr" ? "fr-FR" : "en-US"))

interface MultiValue {
	id: string
	value?: any
	href?: string | null
}

type AggregationType = "sum" | "average" | "concatenate" | "choice" | "none"

type Value = string | number | Date | boolean | Array<any> | null | undefined

const Yes = "yes"
const No = "no"
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const tooltipPositioning = { placement: "left" as const, gutter: 10, flip: false, strategy: "fixed" as const }

const props = withDefaults(
	defineProps<{
		type?: string
		title?: string
		hideTitle?: boolean
		name: string
		value: Value
		options?: string[] | { value: string; label: string }[]
		placeholder?: string
		debounceMs?: number
		disabled?: boolean
		readonly?: boolean
		multiple?: boolean
		rounding?: number
		symbol?: string
		info?: boolean
		tooltip?: string
		multiValues?: MultiValue[] | null
		aggregationType?: AggregationType
		expandAllValues?: boolean
		collapsibleRegistry?: Set<number>
		download?: boolean
		copyTitle?: string
	}>(),
	{
		type: "text",
		download: false,
		options: () => [] as string[],
		debounceMs: 300,
		disabled: false,
		readonly: false,
		multiple: false,
		symbol: "",
		expandAllValues: false,
		info: false,
		tooltip: "",
		aggregationType: "none",
	}
)

const { t } = useI18n()

const urlHref = computed(() => toExternalHref(typeof props.value === "string" ? props.value : undefined) ?? undefined)

// stacks a readonly row's label above its value once the value wraps past ~2 lines
function markLongValue(el: HTMLElement): void {
	const valueEl = el.querySelector<HTMLElement>(".value-text")
	if (!valueEl) return
	const lineHeight = Number.parseFloat(window.getComputedStyle(valueEl).lineHeight) || 16
	const lineCount = valueEl.scrollHeight / lineHeight
	el.classList.toggle("long", lineCount > 2.3)
}

const vAutoStackLongValue = {
	mounted: (el: HTMLElement) => markLongValue(el),
	updated: (el: HTMLElement) => markLongValue(el),
}

function formatIdsList(ids: string[]): string {
	return ids.map(id => id.replace(/ /g, " ")).join(", ")
}

const emit = defineEmits<{
	(e: "input", field: { name: string; value: any }): void
}>()
const select = ref<HTMLSelectElement | null>(null)
const isMultiMode = computed(() => mergedMultiValues.value.length > 0)
const isSimpleValuesList = computed(
	() => !!props.multiValues && props.multiValues.every(item => item.id === formatMultiValue(item.value))
)
const mergedMultiValues = computed(() => {
	const groups = new Map<string, { ids: string[]; value: any; href?: string | null; formattedValue: string }>()

	for (const item of props.multiValues ?? []) {
		const formattedValue = formatMultiValue(item.value)
		if (formattedValue === "N/A") continue
		const key = `${formattedValue}|${item.href ?? ""}`
		const group = groups.get(key)
		if (group) {
			group.ids.push(item.id)
		} else {
			groups.set(key, { ids: [item.id], value: item.value, href: item.href, formattedValue })
		}
	}

	return [...groups.values()]
})
const manualBreakdownExpanded = ref<boolean | null>(null)
const isBreakdownAutoExpanded = computed(() => mergedMultiValues.value.length <= 2)
const isBreakdownExpanded = computed(() => {
	if (manualBreakdownExpanded.value !== null) return manualBreakdownExpanded.value
	return isBreakdownAutoExpanded.value
})
watch(
	() => props.expandAllValues,
	expandAll => {
		manualBreakdownExpanded.value = expandAll
	},
	{ immediate: props.expandAllValues }
)

const isEditingNumber = ref(false)
const numberInput = ref("")

const dateModel = computed<Date | null>({
	get() {
		const v = props.value
		if (v instanceof Date) return parseISODate(v.toISOString().split("T")[0])
		if (typeof v === "string" && ISO_DATE_RE.test(v)) return parseISODate(v)
		return null
	},
	set(d) {
		const iso = d ? formatDateToISO(d) : null
		emit("input", { name: props.name, value: iso })
	},
})

function formatNumberInput(value: Value, editing = false): string {
	if (value == null || value === "") return ""
	const number = typeof value === "number" ? value : parseQuantityInput(String(value))
	if (typeof number !== "number" || !Number.isFinite(number)) return ""
	return editing
		? formatQuantity(number, { useGrouping: false, maximumSignificantDigits: 21 })
		: formatNumber(number, props.rounding)
}

const numberInputValue = computed(() => {
	return isEditingNumber.value ? numberInput.value : formatNumberInput(props.value)
})

const numberFormatOptions = computed(() => {
	const rounding = props.rounding
	if (rounding == null || !Number.isFinite(rounding) || rounding <= 0) return {}

	const decimals = fractionDigitCount(rounding)
	return { maximumFractionDigits: decimals }
})

function fractionDigitCount(value: number): number {
	const [, fraction] = String(value).split(".")
	return fraction?.length ?? 0
}

function formatNumber(value: number, rounding: number | undefined): string {
	if (rounding == null || !Number.isFinite(rounding) || rounding <= 0) {
		return formatQuantity(value)
	}

	return formatQuantity(roundTo(value, rounding) ?? value, numberFormatOptions.value)
}

const listOptions = computed(() => {
	if (!props.options || props.options.length === 0) return []

	if (typeof props.options[0] === "object" && "value" in props.options[0]) {
		return props.options as { value: string; label: string }[]
	}

	return (props.options as string[]).map(o => ({ value: o, label: String(o) }))
})

const EMPTY_DISPLAY = "—"

const displayValue = computed<string>(() => {
	const value = props.value

	if (isMissing(value)) return EMPTY_DISPLAY

	if (props.type === "number") return formatNumberInput(value) || EMPTY_DISPLAY

	if (props.type === "checkbox") {
		if (value === true || value === Yes) return t("transaction.card.yes")
		if (value === false || value === No) return t("transaction.card.no")
		return EMPTY_DISPLAY
	}

	if (props.type === "select") {
		const values = Array.isArray(value) ? value : [value]
		return values.map(v => listOptions.value.find(o => o.value === v)?.label ?? String(v)).join(", ")
	}

	return Array.isArray(value) ? value.join(", ") : String(value)
})

const selectValue = ref<string | number | null | (string | number | null)[]>(null)

const checkboxValue = ref<string | null>(null)
const syncDepth = ref(0)

const checkboxOptions = computed(() => [
	{ value: "yes", label: t("transaction.card.yes") },
	{ value: "no", label: t("transaction.card.no") },
])

function onChoiceSelect(value: any) {
	emit("input", { name: props.name, value })
}

function isMissing(value: Value) {
	return value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)
}

function formatMultiValue(value: any): string {
	if (value == null) return "N/A"
	if (Array.isArray(value)) {
		const formatted = value.map(formatSingleValue).filter(v => v !== "N/A")
		return formatted.length ? formatted.join(", ") : "N/A"
	}
	return formatSingleValue(value)
}

function formatSingleValue(rawValue: any): string {
	const value = rawValue && typeof rawValue === "object" && "value" in rawValue ? rawValue.value : rawValue
	if (props.type === "number") {
		const n = Number(value)
		return Number.isFinite(n) ? formatNumber(n, props.rounding) : String(value)
	}
	if (props.type === "date") {
		if (!value) return "N/A"
		if (typeof value === "string" && ISO_DATE_RE.test(value)) {
			const d = parseISODate(value)
			return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-US")
		}

		const numVal = Number(value)
		if (!isNaN(numVal) && String(value).length === 13) {
			const d = new Date(numVal)
			return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-US")
		}

		if (value instanceof Date) {
			const d = parseISODate(value.toISOString().split("T")[0])
			return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-US")
		}
		const d = new Date(String(value))
		return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-US")
	}
	return String(value)
}

const instanceId = getCurrentInstance()!.uid
const hasCollapsibleBreakdown = computed(() => props.aggregationType === "choice" && !isBreakdownAutoExpanded.value)
watch(
	hasCollapsibleBreakdown,
	value => {
		if (value) props.collapsibleRegistry?.add(instanceId)
		else props.collapsibleRegistry?.delete(instanceId)
	},
	{ immediate: true }
)
onUnmounted(() => props.collapsibleRegistry?.delete(instanceId))

const onInput = debounce((event: Event) => {
	const target = event.target as HTMLInputElement | HTMLSelectElement
	let value: Value = target.value

	if (value === "") value = null
	else if (props.type === "checkbox") value = target.value === Yes ? true : target.value === No ? false : null
	else if (props.type === "select" && props.multiple && target instanceof HTMLSelectElement)
		value = Array.from(target.selectedOptions).map(opt => opt.value)

	emit("input", { name: props.name, value })
}, props.debounceMs)

function onTextInput(event: Event) {
	onInput(event)
	if (props.debounceMs === 0) onInput.flush()
}

const emitNumberInput = debounce((rawValue: string) => {
	const value = parseQuantityInput(rawValue)
	if (value !== undefined) emit("input", { name: props.name, value })
}, props.debounceMs)

function onNumberFocus() {
	isEditingNumber.value = true
	numberInput.value = formatNumberInput(props.value, true)
}

function onNumberBlur() {
	emitNumberInput.flush()
	isEditingNumber.value = false
	numberInput.value = formatNumberInput(props.value)
}

function onNumberInput(event: Event) {
	const target = event.target as HTMLInputElement
	numberInput.value = target.value
	emitNumberInput(target.value)
	if (props.debounceMs === 0) emitNumberInput.flush()
}

// watches
watch(
	() => props.value,
	newValue => {
		syncDepth.value += 1

		if (props.multiple) selectValue.value = Array.isArray(newValue) ? newValue : newValue ? [newValue] : []
		else selectValue.value = Array.isArray(newValue) ? newValue[0] ?? null : newValue ?? null

		if (newValue === true || newValue === "yes") checkboxValue.value = "yes"
		else if (newValue === false || newValue === "no") checkboxValue.value = "no"
		else checkboxValue.value = null

		if (!isEditingNumber.value) numberInput.value = formatNumberInput(newValue)

		void nextTick(() => {
			syncDepth.value = Math.max(0, syncDepth.value - 1)
		})
	},
	{ immediate: true }
)

watch(selectValue, newValue => {
	if (syncDepth.value > 0) return
	emit("input", { name: props.name, value: newValue })
})

watch(checkboxValue, newValue => {
	if (syncDepth.value > 0) return
	let boolValue: boolean | null = null
	if (newValue === "yes") boolValue = true
	else if (newValue === "no") boolValue = false
	emit("input", { name: props.name, value: boolValue })
})

onMounted(() => {
	if (select.value && select.value.multiple) select.value.size = select.value.options.length
})
</script>

<style scoped>
.editable-field {
	display: flex;
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	gap: var(--spacing-xs);
	position: static;
	overflow: visible;
	z-index: auto;

	* {
		overflow: visible !important;
	}

	&.is-missing {
		.title {
			color: var(--neutral-600);
		}
	}

	&.is-readonly {
		align-items: baseline;
		padding: 6px 0;
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		.title,
		.value {
			align-self: baseline;
		}

		&.long {
			flex-direction: column;
			align-items: flex-start;
			gap: var(--spacing-xxs);

			.title {
				flex: 0 0 auto;
			}

			.value {
				justify-content: flex-start;
			}

			.value-text {
				width: 100%;
				text-align: left;
			}
		}
	}

	.title {
		flex: 0 0 var(--flex-basis-field);
		align-self: center;
		font-weight: var(--font-weight-medium);
		color: var(--neutral-600);

		&.has-tooltip {
			cursor: help;
		}

		&.info {
			color: var(--neutral-900);

			&::before {
				content: "⤷ ";
				font-weight: var(--font-weight-bold);
				font-size: var(--font-size-small);
			}
		}
	}

	.tooltip-content {
		padding: var(--spacing-sm) var(--spacing-md);
	}

	.copy {
		align-self: center;
	}

	:deep(.hover-card-wrapper) {
		flex: 0 0 var(--flex-basis-field);
		align-self: center;
	}

	.value {
		position: relative;
		flex: 1;
		min-width: 0;
		align-self: flex-end;
		display: flex;
		justify-content: flex-end;
		overflow: hidden;

		&.has-symbol:not(.readonly) {
			:is(input:not([type="checkbox"])) {
				padding-right: var(--spacing-xl);
			}
		}

		&.has-symbol.readonly {
			gap: var(--spacing-xxs);
		}

		a:not(.link-item):not(.link-pill):not(.open-link) {
			max-width: 100%;
			overflow: hidden !important;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.link-pill {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-xxs);
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--neutral-100);
			color: var(--neutral-700);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			text-decoration: none;
			transition:
				background-color 0.2s ease,
				color 0.2s ease;

			&:hover {
				background-color: var(--neutral-200);
				color: var(--neutral-900);
			}
		}

		.url-field {
			flex: 1;
			min-width: 0;
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);

			input {
				flex: 1;
				min-width: 0;
			}

			.open-link {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				color: var(--neutral-600);

				&:hover {
					color: var(--primary-green);
				}
			}
		}

		.value-text {
			flex: 1;
			min-width: 0;
			color: var(--neutral-900);
			text-align: end;
			line-height: var(--line-height-normal);
			overflow-wrap: break-word;

			&.empty {
				color: var(--neutral-400);
			}
		}

		:is(input:not([type="checkbox"]), select, textarea) {
			width: 100%;
			height: var(--spacing-xl);
			padding: var(--spacing-xs);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background-color: var(--neutral-0);

			&:focus {
				outline: none;
				border-color: var(--primary-green);
			}

			&:disabled {
				background-color: var(--neutral-0);
				color: var(--neutral-600);
				cursor: not-allowed;
			}
		}

		textarea {
			min-height: var(--height-header);
			line-height: var(--line-height-normal);
			resize: vertical;
		}
	}

	.symbol:not(:empty) {
		font-size: var(--font-size-small);
		color: var(--neutral-600);
		flex-shrink: 0;

		.value:not(.readonly) & {
			position: absolute;
			top: 50%;
			right: var(--spacing-sm);
			transform: translateY(-50%);
			pointer-events: none;
		}
	}

	:deep(.single-date) {
		&[data-disabled="true"],
		&.disabled {
			.date-trigger {
				pointer-events: none !important;
				opacity: 0.5;
				cursor: not-allowed !important;
			}

			.date-input {
				cursor: not-allowed !important;

				&:focus {
					border-color: var(--neutral-200) !important;
					box-shadow: none !important;
				}
			}
		}
	}

	.link-list {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		width: 100%;
		max-width: var(--min-width-container);
		overflow: visible !important;

		.link-item {
			white-space: nowrap;
			overflow-x: auto !important;
			overflow-y: hidden !important;
			display: block;

			scrollbar-width: none;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}

			&:hover {
				text-decoration: underline;
			}
		}
	}
}

.multi-values-section {
	margin-top: calc(var(--spacing-xxs) - var(--spacing-md));
}

.values-breakdown-toggle {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-xxs);
	margin-top: var(--spacing-xxs);
	padding: 0;
	border: none;
	background: none;
	font-family: var(--font-family-primary);
	font-size: var(--font-size-xs);
	color: var(--neutral-600);
	cursor: pointer;

	&:hover {
		color: var(--primary-green);
	}
}

.values-breakdown {
	margin-top: var(--spacing-xs);
	display: flex;
	flex-direction: column;

	&.simple-list {
		flex-direction: row;
		flex-wrap: wrap;
		gap: var(--spacing-xxs);
	}
}

.choice-option {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xxs);
	min-width: 0;
	cursor: pointer;

	&.disabled {
		cursor: not-allowed;
		pointer-events: none;
		opacity: 0.7;

		a {
			pointer-events: auto;
			cursor: pointer;
		}
	}

	&:hover {
		background-color: var(--neutral-100);
	}

	&.full-width {
		display: block;
		padding: var(--spacing-xs) var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-top: none;

		&:first-child {
			border-top: var(--border-width-thin) solid var(--neutral-200);
			border-top-left-radius: var(--border-radius-base);
			border-top-right-radius: var(--border-radius-base);
		}

		&:last-child {
			border-bottom-left-radius: var(--border-radius-base);
			border-bottom-right-radius: var(--border-radius-base);
		}
	}

	.simple-list & {
		flex-direction: row;
		align-items: baseline;
		gap: var(--spacing-sm);
		padding: var(--spacing-xxs) var(--spacing-xs);
		background-color: var(--neutral-100);
		border-radius: var(--border-radius-sm);
	}
}

.choice-option.full-width.linked {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	gap: var(--spacing-xs);
}

.transaction-label {
	font-size: var(--font-size-xs);
	color: var(--neutral-600);
	margin: 0;
}

.full-width .transaction-label {
	display: inline;
}

.full-width .transaction-label::after {
	content: ": ";
}

.full-width.linked .transaction-label {
	display: block;
	flex-shrink: 0;

	&::after {
		content: none;
	}

	a {
		color: var(--neutral-700);
		text-decoration: underline;

		&:hover {
			color: var(--primary-green);
		}
	}
}

.choice-value {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: var(--font-size-small);
	color: var(--neutral-900);
	text-transform: capitalize;
	margin: 0;
}

.choice-value a {
	color: var(--primary-green);
	text-decoration: underline;
}

.choice-value a:hover {
	color: color-mix(in srgb, var(--primary-green) 82%, black);
}

.full-width .choice-value {
	display: inline;
	overflow: visible;
	text-overflow: clip;
	white-space: normal;
	word-break: break-word;
}

.full-width.linked .choice-value {
	display: block;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
